import React, { useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { CompareModes } from 'context/entityContext';
import metrics, { IMetric } from 'maps/metrics';

import { useTopicFilters } from './utils/useTopicFilters';
import { ReactComponent as IconClearFilter } from 'icons/icon-clear-filter.svg';
import { useFilterUrlHandlers } from './utils/useFilterUrlHandlers';
import useMedia from 'hooks/useMedia';
import { logToAmplitude } from 'helpers/logAmplitudeEvent';
import MobileTopicFiltersContainer from './MobileTopicFiltersContainer';
import {
  ClearButton,
  Button,
  ClearButtonCloseIcon,
} from './TopicFilters.styles';
import DesktopFiltersContainer from './DesktopFiltersContainer';
import TimeframeDropdown from 'components/base/TopicFiltersDropdown/TimeframeDropdown';
import getFilterCount from './utils/getFilterCount';
import { SalesTypes } from 'context/entityContext';
import {
  filtersForTopic,
  TopicFilterNames,
  viewSettingsForTopic,
  TopicsWithFilters,
  TopicViewSettingNames,
} from './shared';
import useComponentSettings from 'hooks/useComponentSettings';
import getSchemas, {
  ViewSettings,
} from 'schemas/componentSettings/viewSettings';
import { SalesTypeValues } from 'maps/sales-types';
import { IRegionProps } from 'types/shared';
import CountryFilterContainer from './CountryFilterContainer';
import MediaDropdown from 'components/base/TopicFiltersDropdown/MediaDropdown';
import ReleaseDropdown from 'components/base/TopicFiltersDropdown/ReleaseDropdown';
import PlatformDropdown from 'components/base/TopicFiltersDropdown/PlatformDropdown';
import DSPFilterContainer from './DSPFilterContainer';
import RegionFilterContainer from './RegionFilterContainer';
import GenreFilterContainer from 'components/base/TopicFiltersDropdown/GenreDropdown';
import { EntityTypes } from 'context/entityContext';
import { loadRegions } from 'redux/reducers/regions';
import { RootState } from 'redux/rootReducer';

interface IProps {
  topic: TopicsWithFilters;
  compareMode?: CompareModes;
  selectedSalesType?: SalesTypes | SalesTypeValues;
  isTimePeriodDisabled?: boolean;
  isTerritoryFilterDisabled?: boolean;
  isRegionFilterDisabled?: boolean;
  entityType?: EntityTypes;
}

const EMPTY_ARRAY: readonly [] = [];

const TopicFilters: React.FC<IProps> = (props) => {
  const {
    topic,
    compareMode,
    selectedSalesType,
    isTimePeriodDisabled,
    isTerritoryFilterDisabled,
    isRegionFilterDisabled,
    entityType,
  } = props;

  const dispatch = useDispatch();
  const isSmallScreen = useMedia('(max-width: 47em)');

  const viewSettingSchema = getSchemas();
  const [viewSettings, setViewSettings] = useComponentSettings<ViewSettings>(
    'viewSettings',
    viewSettingSchema
  );

  const validFilters = filtersForTopic[topic] ?? EMPTY_ARRAY;
  const validViewSettings = viewSettingsForTopic[topic] ?? EMPTY_ARRAY;

  const [filters, recentFilters] = useTopicFilters();
  const [setFilterUrl, clearFilterUrl] = useFilterUrlHandlers(
    filters,
    validFilters
  );

  useEffect(() => {
    if (entityType === 'release' && topic === 'streaming') {
      setFilter('media', 'audio');
    }
  }, [topic]);

  const regions = useSelector((state: RootState) => state.regions.data);
  const topicFiltersFulfilled = useSelector(
    (state: RootState) => state.topicFilters.fulfilled
  );

  useEffect(() => {
    dispatch(loadRegions());
  }, [dispatch]);

  useEffect(() => {
    if (topic === 'topLists') {
      clearFilterUrl('streaming');
    }
  }, [topic]);

  useEffect(() => {
    if (filters?.region && regions.length) {
      const userRegionArray = Array.isArray(filters.region)
        ? filters.region
        : [filters.region];
      const regionIds = regions.map((allReg: IRegionProps) => allReg.id);
      const allUserRegionsCorrect = userRegionArray.every((reg: string) =>
        regionIds.includes(reg)
      );

      if (!allUserRegionsCorrect) {
        clearFilterUrl('region');
      }
    }
  }, [filters, clearFilterUrl, regions]);

  useEffect(() => {
    if (isTimePeriodDisabled) {
      if (filters.release) {
        clearFilterUrl('release');
      }
      if (filters.genre) {
        clearFilterUrl('genre');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTimePeriodDisabled, filters]);

  useEffect(() => {
    if (
      selectedSalesType === 'streaming' &&
      !isFilterValidForTopic('download')
    ) {
      // if (selectedSalesType === 'streaming') {
      clearFilterUrl('download');
    }
    if (selectedSalesType === 'downloads') {
      clearFilterUrl('streaming');
    }
    if (selectedSalesType === 'physical') {
      clearFilterUrl('media');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSalesType]);

  useEffect(() => {
    if (!isFilterValidForTopic('platform') && filters?.platform) {
      clearFilterUrl('platform');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validFilters, filters]);

  const memoizedMetrics = useMemo(() => {
    return [metrics.twtd, metrics.lw];
  }, []);

  const memoizedMediaTypes = useMemo(() => {
    const mediaTypes = [
      {
        name: 'audio',
        label: 'Audio',
      },
    ];
    if (entityType !== 'release' || topic !== 'streaming') {
      mediaTypes.push({
        name: 'video',
        label: 'Video',
      });
    }
    return mediaTypes;
  }, [topic]);

  const memoizedReleaseTypes = useMemo(() => {
    return [
      {
        name: 'frontline',
        label: 'Frontline',
        tooltipContent: 'First global release date is less than 18 months ago',
      },
      {
        name: 'catalogue',
        label: 'Catalogue',
        tooltipContent: 'First global release date is more than 18 months ago',
      },
    ];
  }, []);

  const isFilterValidForTopic = (filterName: TopicFilterNames): boolean => {
    return validFilters?.includes(filterName) ?? false;
  };

  const isViewSettingValidForTopic = (
    settingName: TopicViewSettingNames
  ): boolean => {
    return validViewSettings?.includes(settingName) ?? false;
  };

  const getViewSettingsCount = (): number => {
    if (validViewSettings.length === 0) {
      return 0;
    }
    return validViewSettings.length - 1;
  };

  const filterCount =
    getFilterCount(filters, validFilters) + getViewSettingsCount();

  useEffect(() => {
    if (topicFiltersFulfilled && filterCount > 0) {
      logToAmplitude('Page has been loaded with a global filter active');
    }
  }, [topicFiltersFulfilled]);

  const setFilter = (type: TopicFilterNames, value: string): void => {
    // Running this synchronously causes a visible freeze in the UI,
    // therefore this timeout hack is needed to keep the UX smooth
    setTimeout(() => {
      setFilterUrl(type, value);
    }, 0);
  };

  const setViewSetting = (
    type: TopicViewSettingNames,
    value: string | boolean
  ): void => {
    // Running this synchronously causes a visible freeze in the UI,
    // therefore this timeout hack is needed to keep the UX smooth
    setTimeout(() => {
      setViewSettings({
        ...viewSettings,
        [type as string]: value,
      });
    }, 0);
  };

  const TopicFiltersContainer = isSmallScreen
    ? MobileTopicFiltersContainer
    : DesktopFiltersContainer;

  const mobileFilterTitle = filterCount === 0 ? 'Filters' : 'Filters applied';

  const selectedMediaFilter = useMemo(
    () =>
      memoizedMediaTypes.find(
        (item: any): boolean => item.name === filters.media
      ),
    [memoizedMediaTypes, filters.media]
  );

  const selectedReleaseFilter = useMemo(
    () =>
      isTimePeriodDisabled
        ? null
        : memoizedReleaseTypes.find(
            (item: any): boolean => item.name === filters.release
          ),
    [memoizedReleaseTypes, filters.release, isTimePeriodDisabled]
  );

  const selectedTimeframeFilter = useMemo(
    () =>
      memoizedMetrics.find(
        (metric: IMetric): boolean => metric.value === viewSettings.timeframe
      ),
    [memoizedMetrics, viewSettings.timeframe]
  );

  const clearMediaFilter = (): void => {
    if (filters.media) {
      clearFilterUrl('media');
      logToAmplitude('Filter has been updated', { type: 'Media Type' });
    }
  };

  const isReleaseFilterDisplayed =
    (topic === 'view' &&
      (entityType === 'artist' ||
        entityType === 'release' ||
        entityType === 'project')) ||
    topic === 'home';

  return (
    <>
      {validFilters?.length > 0 && (
        <TopicFiltersContainer
          filterCount={filterCount}
          title={mobileFilterTitle}
        >
          {isViewSettingValidForTopic('timeframe') && (
            <TimeframeDropdown
              onChange={(item: IMetric): void =>
                setViewSetting('timeframe', item.value)
              }
              items={memoizedMetrics}
              selectedItem={selectedTimeframeFilter}
              defaultLabel="Timeframe"
            />
          )}
          {isFilterValidForTopic('region') && (
            <RegionFilterContainer
              setFilter={setFilter}
              clearFilterUrl={clearFilterUrl}
              filters={filters}
              compareMode={compareMode}
              isSmallScreen={isSmallScreen}
              regionsFromState={regions}
              disabled={isRegionFilterDisabled}
            />
          )}
          {isFilterValidForTopic('country') && (
            <CountryFilterContainer
              setFilter={setFilter}
              clearFilterUrl={clearFilterUrl}
              filters={filters}
              recentFilters={recentFilters}
              disabled={isTerritoryFilterDisabled}
            />
          )}
          {(isFilterValidForTopic('streaming') ||
            isFilterValidForTopic('download')) && (
            <DSPFilterContainer
              setFilter={setFilter}
              clearFilterUrl={clearFilterUrl}
              filters={filters}
              recentFilters={recentFilters}
              isSmallScreen={isSmallScreen}
              isStreamingAvailable={isFilterValidForTopic('streaming')}
              isDownloadsAvailable={isFilterValidForTopic('download')}
            />
          )}
          {isFilterValidForTopic('media') && (
            <MediaDropdown
              onChange={(item: any): void => {
                setFilter('media', item?.name);
                logToAmplitude('Filter has been updated', {
                  type: 'Media Type',
                  value: item?.name,
                });
              }}
              items={memoizedMediaTypes}
              selectedItem={selectedMediaFilter}
              defaultLabel="All"
              itemToString={(item): string => item?.name}
              disabled={selectedSalesType === 'physical'}
              clearFilter={clearMediaFilter}
            >
              <Button
                borderColor={'LightGrey'}
                borderWidth={'1px'}
                borderStyle={'solid'}
                py={4}
                isActive={!!selectedMediaFilter}
                onClick={clearMediaFilter}
                isClearButton
              >
                <IconClearFilter />
                Clear this filter
              </Button>
            </MediaDropdown>
          )}
          {isFilterValidForTopic('genre') && (
            <GenreFilterContainer
              clearFilterUrl={clearFilterUrl}
              defaultLabel="All"
              filters={filters}
              recentFilters={recentFilters}
              setFilter={setFilter}
            />
          )}
          {isFilterValidForTopic('platform') && (
            <PlatformDropdown
              setFilter={setFilter}
              clearFilterUrl={clearFilterUrl}
              filters={filters}
            />
          )}
          {isFilterValidForTopic('release') && isReleaseFilterDisplayed && (
            <ReleaseDropdown
              onChange={(item: any): void => setFilter('release', item?.name)}
              items={memoizedReleaseTypes}
              selectedItem={selectedReleaseFilter}
              defaultLabel="All"
              itemToString={(item: any): string => item?.name}
              isSmallScreen={isSmallScreen}
              disabled={isTimePeriodDisabled}
              clearFilter={(): void => clearFilterUrl('release')}
            >
              <Button
                borderColor={'LightGrey'}
                borderWidth={'1px'}
                borderStyle={'solid'}
                py={4}
                isActive={!!selectedReleaseFilter}
                onClick={(): void => clearFilterUrl('release')}
                isClearButton
              >
                <IconClearFilter />
                Clear this filter
              </Button>
            </ReleaseDropdown>
          )}
          <ClearButton
            isActive={filterCount > 0}
            onClick={(): void => {
              clearFilterUrl();
              logToAmplitude('Filter has been updated', { type: 'Reset all' });
            }}
            isSmallScreen={isSmallScreen}
          >
            {filterCount > 0 && <ClearButtonCloseIcon />}
            {isSmallScreen ? 'Reset All Filters' : 'Reset all'}
          </ClearButton>
        </TopicFiltersContainer>
      )}
    </>
  );
};

export default TopicFilters;
